import React, { useEffect, useMemo, useState } from "react"
import { FileDown, Printer } from "lucide-react"
import { BaseballDiamond, baseballPositions } from "./BaseballDiamond"
import { Badge, Button, Card } from "./ui"

const PRINT_PREFERENCE_KEY = "print_include_inning_pages"
export function PrintableLineup({ game, lineups, battingOrder, players }) {
  const [includeInningPages, setIncludeInningPages] = useState(() => {
    try {
      const saved = window.localStorage.getItem(PRINT_PREFERENCE_KEY)
      return saved === null ? true : JSON.parse(saved)
    } catch { return true }
  })

  useEffect(() => {
    try { window.localStorage.setItem(PRINT_PREFERENCE_KEY, JSON.stringify(includeInningPages)) } catch { /* Preference persistence is optional. */ }
  }, [includeInningPages])

  const innings = useMemo(() => Array.from({ length: game.innings }, (_, index) => index + 1), [game.innings])
  const availablePlayers = useMemo(() => {
    const availability = new Map(game.player_availabilities.map(entry => [entry.player_id, entry.is_available]))
    return players.filter(player => availability.get(player.id) !== false)
  }, [game.player_availabilities, players])
  const lineupsByInning = useMemo(() => {
    const result = new Map()
    innings.forEach(number => result.set(number, new Map()))
    lineups.forEach(item => result.get(item.inning)?.set(item.field_position, item.player_id))
    return result
  }, [innings, lineups])
  const orderedPlayers = useMemo(() => {
    const byId = new Map(availablePlayers.map(player => [player.id, player]))
    const ordered = battingOrder.map(item => byId.get(typeof item === "string" ? item : item.player_id)).filter(Boolean)
    return ordered.length ? ordered : availablePlayers
  }, [availablePlayers, battingOrder])

  function updateInningPages(event) {
    setIncludeInningPages(event.target.checked)
  }

  function printLineup() {
    const printWindow = window.open("", "_blank")
    if (!printWindow) {
      window.alert("Unable to open the print preview. Allow pop-ups for this site and try again.")
      return
    }
    printWindow.opener = null
    printWindow.document.write(buildPrintDocument({ game, innings, lineupsByInning, orderedPlayers, players: availablePlayers, includeInningPages }))
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
  }

  return <div className="print-workspace">
    <Card className="panel print-controls">
      <div className="panel__header">
        <div><p className="eyebrow">Lineup packet</p><h2>Print options</h2></div>
        <Badge tone="blue">A4 landscape</Badge>
      </div>
      <label className="print-option" htmlFor="include-inning-pages">
        <span><strong>Include individual inning pages</strong><small>{includeInningPages ? `Adds ${game.innings} field diagrams and player lists after the summary.` : "Prints only the batting order and position summary."}</small></span>
        <span className="toggle-control"><input id="include-inning-pages" name="include_inning_pages" type="checkbox" checked={includeInningPages} onChange={updateInningPages} /><span aria-hidden="true" /></span>
      </label>
      <div className="button-row">
        <Button type="button" onClick={printLineup}><Printer size={16} /> Print lineup</Button>
        <span className="print-pdf-hint"><FileDown size={16} /> Choose “Save as PDF” in the print dialog to export a PDF.</span>
      </div>
    </Card>

    <div className="print-preview-heading"><div><p className="eyebrow">Preview</p><h2>Game sheets</h2></div><span>{includeInningPages ? `${game.innings + 1} pages` : "1 page"}</span></div>
    <SummarySheet game={game} innings={innings} lineupsByInning={lineupsByInning} orderedPlayers={orderedPlayers} battingOrderSet={battingOrder.length > 0} />
    {includeInningPages ? innings.map(number => <InningSheet key={number} game={game} inning={number} assignments={lineupsByInning.get(number)} players={availablePlayers} />) : null}
  </div>
}

function SummarySheet({ game, innings, lineupsByInning, orderedPlayers, battingOrderSet }) {
  return <Card className="print-sheet print-sheet--summary">
    <PrintHeader game={game} title={`${game.team.name} vs ${game.opponent}`} />
    {!battingOrderSet ? <p className="print-warning">Batting order has not been saved. Available players are shown in roster order.</p> : null}
    <div className="print-table-scroll"><table className="print-summary-table"><thead><tr><th>Batting order</th>{innings.map(number => <th key={number}>Inning {number}</th>)}</tr></thead><tbody>{orderedPlayers.map((player, index) => <tr key={player.id}><th>{index + 1}. {jersey(player)} {player.first_name} {player.last_name}</th>{innings.map(number => <td key={number}>{positionForPlayer(lineupsByInning.get(number), player.id)}</td>)}</tr>)}</tbody></table></div>
  </Card>
}

function InningSheet({ game, inning, assignments, players }) {
  const sortedPlayers = [...players].sort(compareJerseys)
  return <Card className="print-sheet print-sheet--inning">
    <PrintHeader game={game} title={`Inning ${inning} lineup`} />
    <div className="print-inning-grid">
      <table className="print-roster-table"><thead><tr><th>Jersey</th><th>Player name</th><th>Position</th></tr></thead><tbody>{sortedPlayers.map(player => <tr key={player.id}><td>{jersey(player, "—")}</td><th>{player.first_name} {player.last_name}</th><td>{positionForPlayer(assignments, player.id)}</td></tr>)}</tbody></table>
      <div className="print-diamond"><BaseballDiamond assignments={assignments} players={players} hasFourthOutfielder={game.team.has_fourth_outfielder} readOnly /></div>
    </div>
  </Card>
}

function PrintHeader({ game, title }) {
  return <header className="print-sheet__header"><div><p className="eyebrow">{game.team.name}</p><h2>{title}</h2></div><p>{formatGameMeta(game)}</p></header>
}

function positionForPlayer(assignments, playerId) {
  for (const [position, assignedId] of assignments || []) if (assignedId === playerId) return position
  return "SIT"
}

function jersey(player, fallback = "") {
  return player.jersey_number == null ? fallback : `#${player.jersey_number}`
}

function compareJerseys(a, b) {
  if (a.jersey_number == null && b.jersey_number == null) return `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`)
  if (a.jersey_number == null) return 1
  if (b.jersey_number == null) return -1
  return a.jersey_number - b.jersey_number
}

function formatGameMeta(game) {
  const date = new Date(game.game_date)
  const formatted = date.toLocaleString([], { month: "numeric", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
  return `${formatted}${game.field_number ? ` · Field ${game.field_number}` : ""}`
}

function buildPrintDocument({ game, innings, lineupsByInning, orderedPlayers, players, includeInningPages }) {
  const summaryRows = orderedPlayers.map((player, index) => `<tr><th>${index + 1}. ${escapeHtml(jersey(player))} ${escapeHtml(`${player.first_name} ${player.last_name || ""}`)}</th>${innings.map(number => `<td>${positionForPlayer(lineupsByInning.get(number), player.id)}</td>`).join("")}</tr>`).join("")
  const inningSheets = includeInningPages ? innings.map(number => buildInningSheetHtml({ game, inning: number, assignments: lineupsByInning.get(number), players })).join("") : ""
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(game.team.name)} vs ${escapeHtml(game.opponent)} – Lineup</title><style>${printStyles}</style></head><body>
    <section class="sheet summary"><header>${printHeading(game, `${game.team.name} vs ${game.opponent}`)}</header><table class="summary-table"><thead><tr><th>Batting order</th>${innings.map(number => `<th>Inning ${number}</th>`).join("")}</tr></thead><tbody>${summaryRows}</tbody></table></section>${inningSheets}
  </body></html>`
}

function buildInningSheetHtml({ game, inning, assignments, players }) {
  const rows = [...players].sort(compareJerseys).map(player => `<tr><td>${escapeHtml(jersey(player, "—"))}</td><th>${escapeHtml(`${player.first_name} ${player.last_name || ""}`)}</th><td>${positionForPlayer(assignments, player.id)}</td></tr>`).join("")
  return `<section class="sheet inning"><header>${printHeading(game, `Inning ${inning} lineup`)}</header><div class="inning-grid"><table class="roster-table"><thead><tr><th>Jersey</th><th>Player name</th><th>Position</th></tr></thead><tbody>${rows}</tbody></table>${printDiamond(assignments, players, game.team.has_fourth_outfielder)}</div></section>`
}

function printHeading(game, title) {
  return `<div><span>${escapeHtml(game.team.name)}</span><h1>${escapeHtml(title)}</h1></div><p>${escapeHtml(formatGameMeta(game))}</p>`
}

function printDiamond(assignments, players, hasFourthOutfielder) {
  const playerById = new Map(players.map(player => [player.id, player]))
  const markers = baseballPositions(hasFourthOutfielder).map(([position, x, y]) => {
    const player = playerById.get(assignments?.get(position))
    const content = player ? `<strong>${escapeHtml(shortName(player))}</strong><small>${escapeHtml(jersey(player, position))}</small>` : `<strong>${position}</strong>`
    return `<div class="marker ${player ? "assigned" : ""}" style="left:${x}%;top:${y}%">${content}</div>`
  }).join("")
  return `<div class="field"><div class="infield"></div><div class="basepaths"></div><div class="mound"></div>${markers}</div>`
}

function shortName(player) {
  const value = `${player.first_name}${player.last_name ? ` ${player.last_name[0]}.` : ""}`
  return value.length > 12 ? `${value.slice(0, 11)}…` : value
}

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>'"]/g, character => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character])
}

const printStyles = `
  @page{size:A4 landscape;margin:10mm}*{box-sizing:border-box}html,body{margin:0;background:#fff;color:#182335;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}.sheet{min-height:185mm;page-break-after:always;break-after:page}.sheet:last-child{page-break-after:auto;break-after:auto}header{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin-bottom:18px;padding-bottom:12px;border-bottom:2px solid #1c355c}header span{color:#657187;font-size:9px;font-weight:800;letter-spacing:.12em;text-transform:uppercase}h1{margin:3px 0 0;font-size:24px;line-height:1.1}header p{margin:0;color:#657187;font-size:10px}.summary-table,.roster-table{width:100%;border-collapse:collapse}.summary-table th,.summary-table td,.roster-table th,.roster-table td{padding:7px 8px;border-bottom:1px solid #dfe3ec;font-size:9px;text-align:center}.summary-table thead th,.roster-table thead th{background:#edf0f7;color:#1c355c;font-weight:800}.summary-table tbody th,.roster-table tbody th{text-align:left}.summary-table tbody tr:nth-child(even),.roster-table tbody tr:nth-child(even){background:#f8f9fc}.inning-grid{display:grid;grid-template-columns:42% 58%;gap:18px;align-items:start}.field{position:relative;width:100%;aspect-ratio:1;overflow:hidden;border:2px solid #456f49;border-radius:10px 10px 45% 45%;background:radial-gradient(circle at 50% 100%,#82bd67 0 15%,#6cae59 15% 31%,#78b960 31% 47%,#68aa55 47% 63%,#75b65d 63% 100%)}.infield{position:absolute;left:24%;top:30%;width:52%;aspect-ratio:1;border-radius:22%;background:#d8b47c;transform:rotate(45deg)}.basepaths{position:absolute;left:32%;top:38%;width:36%;aspect-ratio:1;border:2px solid #fff;transform:rotate(45deg)}.mound{position:absolute;left:50%;top:57%;width:20px;height:20px;border:1px solid #9f7645;border-radius:50%;background:#c79861;transform:translate(-50%,-50%)}.marker{position:absolute;z-index:2;display:flex;width:52px;min-height:45px;padding:4px;align-items:center;justify-content:center;flex-direction:column;border:2px solid #1c355c;border-radius:999px;color:#1c355c;background:#fff;transform:translate(-50%,-50%);text-align:center}.marker.assigned{border-color:#fff;color:#fff;background:#1c355c}.marker strong,.marker small{display:block;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.marker strong{font-size:8px}.marker small{font-size:8px;font-weight:800}@media print{body{padding:0}}`
